import * as Sentry from '@sentry/react';
import { apiClient } from '@auto-cargo/protocol-sdk';
import { message } from 'antd';
import { get } from 'lodash';
import { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { IntlProvider } from 'react-intl';
import {
    createRoutesFromChildren,
    matchRoutes,
    RouterProvider,
    useLocation,
    useNavigationType,
} from 'react-router-dom';
import zhCN from '@/locales/zh-CN';
import { router } from '@/router';
import { checkToken } from '@/utils';

/**
 * Styles
 */
import 'antd/dist/reset.css';
import './main.less';

/**
 * Constants
 */
checkToken();

/**
 * Global API error toast.
 * Backend HttpExceptionFilter returns { code, msg }; legacy code reads `message`.
 * Read msg/message/error in order, fall back to status text, then axios default.
 * 401 is already handled by the SDK interceptor (token cleanup + redirect).
 * Skip with `config.skipGlobalErrorMessage = true` when a caller wants to handle it itself.
 * De-dup same url+text within 1.5s to avoid double toasts when a caller still calls message.error.
 */
const recentErrors = new Map<string, number>();
apiClient.interceptors.response.use(
    (response) => response,
    (error: any) => {
        const cfg: any = error?.config || {};
        const status: number | undefined = error?.response?.status;
        if (cfg.skipGlobalErrorMessage || status === 401) {
            return Promise.reject(error);
        }
        const data = error?.response?.data;
        let raw: unknown =
            (data && typeof data === 'object' && (data.msg ?? data.message ?? data.error)) ||
            error?.response?.statusText ||
            error?.message ||
            '请求失败';
        const text = Array.isArray(raw) ? raw.join('; ') : String(raw);
        const key = `${cfg.method || ''} ${cfg.url || ''} ${text}`;
        const now = Date.now();
        const last = recentErrors.get(key);
        if (!last || now - last > 1500) {
            recentErrors.set(key, now);
            message.error(text);
        }
        return Promise.reject(error);
    },
);

Sentry.init({
    dsn: 'https://a05d44c638c3bfca04e3faed43895900@o4508450243739648.ingest.us.sentry.io/4508498883641344',
    enabled: import.meta.env.MODE !== 'development',
    environment: import.meta.env.MODE,
    integrations: [
        Sentry.reactRouterV6BrowserTracingIntegration({
            createRoutesFromChildren,
            matchRoutes,
            useEffect,
            useLocation,
            useNavigationType,
        }),
        Sentry.replayIntegration({
            blockAllMedia: false,
            maskAllInputs: false,
            maskAllText: false,
        }),
    ],
    normalizeDepth: 10,
    release: `admin-fe@${__VERSION__}`,
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    tracePropagationTargets: [
        /^http:\/\/3\.12\.254\.197:3000\/$/,
        /^https:\/\/admin-fe\.actqa\.com\//,
        /^https:\/\/d2jgpuabu7gp7a\.cloudfront\.net\//,
    ],
    tracesSampleRate: 1.0,
    beforeSend: (event) => {
        for (const exception of get(event, 'exception.values', [])) {
            for (const frame of get(exception, 'stacktrace.frames', [])) {
                // 忽略来自扩展的错误
                if (frame?.filename?.includes('chrome-extension://') || frame?.filename?.includes('moz-extension://')) {
                    return null;
                }
            }
        }

        return event;
    },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
    <Sentry.ErrorBoundary fallback={<div>请刷新页面并重试。</div>}>
        <IntlProvider locale="zh-CN" defaultLocale="zh-CN" messages={zhCN}>
            <RouterProvider router={router} fallbackElement="加载中..." />
        </IntlProvider>
    </Sentry.ErrorBoundary>,
);
